import React, { useMemo, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { useAuth } from "../../context/AuthContext.tsx";
import * as S from "./income.style.ts";
import { incomeEventsAtom } from "../../hook/IncomeEventsAtom.ts";
import { ownerPayConfigAtom } from "../../hook/OwnerPayConfigAtom.ts";
import { recurringIncomeAtom } from "../../hook/RecurringIncomeAtom.ts";
import { useMoney } from "../../hook/useMoney.ts";
import { IncomeEvent, IncomeStatus } from "../../types.ts";
import {
  getTrailingIncomeStats,
  ymFromDate,
  ymFromISO,
} from "../../functions/cashflow.ts";
import { modeLabels } from "../../functions/cashflowCopy.ts";
import { persistIncomeEvent } from "../../functions/cashflowSync.ts";
import { trackEvent } from "../../functions/mixpanel.ts";
import Button from "../../components/Button/Button.tsx";
import ModalComponent from "../../components/Modal/Modal.tsx";
import RecurringIncomeSection from "../../components/RecurringIncome/RecurringIncome.tsx";
import { Navigate } from "react-router-dom";
import { userAtom } from "../../hook/UserAtom.ts";

const emptyForm = {
  id: "",
  date: new Date().toISOString().slice(0, 10),
  amount: "",
  client: "",
  label: "",
  status: "received" as IncomeStatus,
  confidence: "100",
};

type FormState = typeof emptyForm;

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const Income = () => {
  const auth = useAuth();
  const [events, setEvents] = useAtom(incomeEventsAtom);
  const config = useAtomValue(ownerPayConfigAtom);
  const currentUser = useAtomValue(userAtom);
  const recurring = useAtomValue(recurringIncomeAtom);
  const { money } = useMoney();
  const labels = modeLabels(config);

  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState("");

  const sorted = useMemo(
    () => [...events].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [events],
  );

  const stats = getTrailingIncomeStats(events, config, new Date(), recurring);
  const currentYM = ymFromDate(new Date());
  const receivedThisMonth = events
    .filter((e) => {
      const ym = ymFromISO(e.date);
      return (
        e.status === "received" &&
        ym.month === currentYM.month &&
        ym.year === currentYM.year
      );
    })
    .reduce((sum, e) => sum + e.amount, 0);
  const pipeline = events
    .filter((e) => e.status === "expected" && e.date >= emptyForm.date)
    .reduce((sum, e) => sum + e.amount * e.confidence, 0);

  const openAdd = () => {
    setForm({ ...emptyForm, status: "received" });
    setError("");
    setIsOpen(true);
  };

  const openEdit = (event: IncomeEvent) => {
    setForm({
      id: event.id,
      date: event.date,
      amount: String(event.amount),
      client: event.client || "",
      label: event.label || "",
      status: event.status,
      confidence: String(Math.round((event.confidence ?? 1) * 100)),
    });
    setError("");
    setIsOpen(true);
  };

  const save = () => {
    const amount = Number(form.amount);
    if (!form.date || !amount || amount <= 0) {
      setError("Enter a date and an amount greater than zero.");
      return;
    }

    const event: IncomeEvent = {
      id: form.id || uuid(),
      date: form.date,
      amount,
      client: form.client.trim() || undefined,
      label: form.label.trim() || undefined,
      status: form.status,
      confidence:
        form.status === "received"
          ? 1
          : Math.max(0, Math.min(1, Number(form.confidence) / 100)),
    };

    const op = form.id ? "update" : "create";
    setEvents((prev) =>
      op === "update"
        ? prev.map((e) => (e.id === event.id ? event : e))
        : [...prev, event],
    );
    persistIncomeEvent(auth, op, event);
    trackEvent(`Income - ${op}`, { status: event.status });
    setIsOpen(false);
  };

  const remove = (event: IncomeEvent) => {
    setEvents((prev) => prev.filter((e) => e.id !== event.id));
    persistIncomeEvent(auth, "delete", event);
    trackEvent("Income - delete");
  };

  if (!currentUser?.hasBudget) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <S.Wrapper>
      <S.Intro>
        {labels.showExpectedIncome
          ? "Log every payment as it lands, and the ones you're still waiting on. Set recurring pay once and it fills itself in."
          : "Set your paycheck once below. Add anything one-off — a bonus, a tax refund, side income — to the log."}
      </S.Intro>

      <S.Stats>
        <S.Stat>
          <div className="label">
            Typical month (last {config.trailingMonths})
          </div>
          <div className="value">{money(stats.median)}</div>
        </S.Stat>
        <S.Stat>
          <div className="label">Received this month</div>
          <div className="value">{money(receivedThisMonth)}</div>
        </S.Stat>
        {labels.showExpectedIncome && (
          <S.Stat>
            <div className="label">Expected pipeline (weighted)</div>
            <div className="value">{money(pipeline)}</div>
          </S.Stat>
        )}
      </S.Stats>

      <RecurringIncomeSection />

      <S.Toolbar>
        <Button buttonSize="medium" handleClick={openAdd}>
          Add one-off income
        </Button>
      </S.Toolbar>

      {sorted.length === 0 ? (
        <S.Empty>No one-off income logged yet.</S.Empty>
      ) : (
        <S.List>
          {sorted.map((event) => (
            <S.Row key={event.id}>
              <span>{event.date}</span>
              <span>
                {event.label || event.client || "Payment"}
                {event.label && event.client ? ` · ${event.client}` : ""}
              </span>
              <span className="amount">{money(event.amount)}</span>
              <span className={`badge ${event.status}`}>
                {event.status === "expected"
                  ? `expected ${Math.round(event.confidence * 100)}%`
                  : "received"}
              </span>
              <span className="actions">
                <Button
                  buttonSize="small"
                  classType="text"
                  handleClick={() => openEdit(event)}
                >
                  Edit
                </Button>
                <Button
                  buttonSize="small"
                  classType="text"
                  handleClick={() => remove(event)}
                >
                  Delete
                </Button>
              </span>
            </S.Row>
          ))}
        </S.List>
      )}

      <ModalComponent
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={form.id ? "Edit income" : "Add income"}
        size="medium"
      >
        <S.Form>
          <div className="row">
            <label>
              Date
              <input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, date: e.target.value }))
                }
              />
            </label>
            <label>
              Amount (USD)
              <input
                type="number"
                value={form.amount}
                placeholder="0"
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="row">
            <label>
              Client
              <input
                type="text"
                value={form.client}
                placeholder="Acme Co."
                onChange={(e) =>
                  setForm((f) => ({ ...f, client: e.target.value }))
                }
              />
            </label>
            <label>
              Description
              <input
                type="text"
                value={form.label}
                placeholder="Retainer, project 2..."
                onChange={(e) =>
                  setForm((f) => ({ ...f, label: e.target.value }))
                }
              />
            </label>
          </div>
          {labels.showExpectedIncome && (
            <label>
              Status
              <select
                value={form.status}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    status: e.target.value as IncomeStatus,
                  }))
                }
              >
                <option value="received">Received</option>
                <option value="expected">Expected / invoiced</option>
              </select>
            </label>
          )}
          {labels.showExpectedIncome && form.status === "expected" && (
            <label>
              How likely is this payment? {form.confidence}%
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={form.confidence}
                onChange={(e) =>
                  setForm((f) => ({ ...f, confidence: e.target.value }))
                }
              />
            </label>
          )}
          {error && <div className="error">{error}</div>}
          <div className="buttons">
            <Button
              buttonSize="small"
              classType="exit"
              handleClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button buttonSize="small" handleClick={save}>
              Save
            </Button>
          </div>
        </S.Form>
      </ModalComponent>
    </S.Wrapper>
  );
};

export default Income;
